import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Camera, Loader2, ShieldCheck, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { friendlyError } from "@/lib/api";
import { institutionOptions } from "@/lib/labels";
import { casesApi, handoffsApi } from "@/lib/endpoints";
import { PhoneVerify } from "@/components/PhoneVerify";

export default function HandoffForm() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState("consent"); // consent | form
  const [accepted, setAccepted] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState("");
  const [institution, setInstitution] = useState("");
  const [name, setName] = useState("");
  const [cedula, setCedula] = useState("");
  const [phone, setPhone] = useState("");
  const [idToken, setIdToken] = useState("");
  const [coords, setCoords] = useState(null);
  const [now] = useState(() => new Date());
  const [submitting, setSubmitting] = useState(false);
  const [caseInfo, setCaseInfo] = useState(null);
  const locale = i18n.language?.startsWith("en") ? "en-US" : "es-VE";

  useEffect(() => {
    casesApi.get(id).then(setCaseInfo).catch(() => toast.error(t("handoff.notFound")));
  }, [id, t]);

  // Auto-capture GPS (kept private; never shown publicly).
  useEffect(() => {
    if (step === "form" && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setCoords(null),
        { enableHighAccuracy: true, timeout: 8000 },
      );
    }
  }, [step]);

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!photo) { toast.error(t("handoff.toastPhoto")); return; }
    if (!institution) { toast.error(t("handoff.toastInstitution")); return; }
    if (!idToken) { toast.error(t("handoff.toastVerify")); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("photo", photo);
      fd.append("institution", institution);
      fd.append("receiver_name", name);
      fd.append("receiver_cedula", cedula);
      fd.append("consent", "true");
      fd.append("firebase_id_token", idToken);
      if (coords) { fd.append("lat", String(coords.lat)); fd.append("lng", String(coords.lng)); }
      await handoffsApi.create(id, fd);
      toast.success(t("handoff.toastRegistered"));
      navigate(`/caso/${id}`);
    } catch (e) {
      toast.error(friendlyError(e, t("handoff.toastError")));
    } finally { setSubmitting(false); }
  };

  const labelCls = "text-sm font-medium text-neutral-900 mb-1.5 block";
  const inputCls = "w-full min-h-[48px] rounded-sm border border-neutral-300 bg-white px-3 text-base focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC] outline-none";

  return (
    <div className="flex flex-col gap-4" data-testid="handoff-form-page">
      <Link to={`/caso/${id}`} className="text-sm text-neutral-500 hover:text-[#0066CC] flex items-center gap-1" data-testid="handoff-back">
        <ArrowLeft className="w-4 h-4" /> {t("handoff.back")}
      </Link>
      <h1 className="text-2xl font-bold text-neutral-900">{t("handoff.title")}</h1>
      {caseInfo && (
        <p className="text-sm text-neutral-500 -mt-2">
          {t("handoff.minor", { name: caseInfo.name_unknown ? t("handoff.noName") : caseInfo.first_name, muni: caseInfo.municipality })}
        </p>
      )}

      {step === "consent" ? (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 flex flex-col gap-3" data-testid="handoff-consent">
          <p className="text-sm text-amber-900 leading-relaxed flex gap-2">
            <ShieldCheck className="w-5 h-5 shrink-0 text-amber-600" /> {t("handoff.consentText")}
          </p>
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
              data-testid="handoff-consent-checkbox" className="mt-1 w-4 h-4" />
            <span className="text-sm font-medium text-neutral-900">
              {t("handoff.consentAccept")}
            </span>
          </label>
          <button disabled={!accepted} onClick={() => setStep("form")} data-testid="handoff-consent-continue"
            className="bg-[#0066CC] text-white hover:bg-[#0052A3] disabled:opacity-50 disabled:cursor-not-allowed rounded-sm min-h-[48px] font-semibold">
            {t("handoff.continue")}
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <label className={labelCls}>{t("handoff.photoLabel")}</label>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={onPhoto}
              data-testid="handoff-photo-input" className="hidden" />
            <button onClick={() => fileRef.current?.click()} data-testid="handoff-photo-btn"
              className="w-full border-2 border-dashed border-neutral-300 rounded-sm min-h-[160px] flex flex-col items-center justify-center gap-2 text-neutral-500 hover:border-[#0066CC] overflow-hidden">
              {preview ? <img src={preview} alt="preview" className="w-full h-40 object-cover" />
                : <><Camera className="w-8 h-8" /><span className="text-sm">{t("handoff.photoBtn")}</span></>}
            </button>
          </div>

          <div>
            <label className={labelCls}>{t("handoff.institutionLabel")}</label>
            <select value={institution} onChange={(e) => setInstitution(e.target.value)}
              data-testid="handoff-institution" className={inputCls}>
              <option value="">{t("common.select")}</option>
              {institutionOptions(t).map((i) => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
          </div>

          <div>
            <label className={labelCls}>{t("handoff.receiverName")}</label>
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="handoff-name" className={inputCls} placeholder={t("handoff.receiverNamePlaceholder")} />
          </div>

          <div>
            <label className={labelCls}>{t("handoff.receiverCedula")}</label>
            <input value={cedula} onChange={(e) => setCedula(e.target.value)} data-testid="handoff-cedula" className={inputCls} placeholder="V-..." />
            <p className="text-xs text-neutral-500 mt-1">{t("handoff.receiverCedulaHint")}</p>
          </div>

          <div>
            <label className={labelCls}>{t("handoff.yourPhone")}</label>
            <PhoneVerify testid="handoff-verify" phone={phone}
              onPhoneChange={setPhone}
              verified={!!idToken} onVerified={setIdToken} onReset={() => setIdToken("")} />
            <p className="text-xs text-neutral-500 mt-1">{t("handoff.yourPhoneHint")}</p>
          </div>

          <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-3 text-xs text-neutral-600 flex flex-col gap-1">
            <span>{t("handoff.dateTime", { dt: now.toLocaleString(locale) })}</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t("handoff.gpsLine", { status: coords ? t("handoff.gpsCaptured") : t("handoff.gpsCapturing") })}</span>
          </div>

          {!idToken && <p className="text-xs text-[#B45309]">{t("handoff.mustVerify")}</p>}
          <button onClick={submit} disabled={submitting || !idToken} data-testid="handoff-submit"
            className="bg-[#166534] text-white hover:opacity-90 disabled:opacity-60 rounded-sm min-h-[52px] font-semibold flex items-center justify-center gap-2">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t("handoff.submit")}
          </button>
        </div>
      )}
    </div>
  );
}
